import React, {useState, useEffect} from "react";

import './Program.members.dialog.scss';
import Modal from "react-modal";
import {useLazyQuery, useMutation} from "@apollo/client";
import gqlSchemaLibrary from "../../services/graphql.schemas";
import {Subject} from "rxjs";
import {useTranslation} from "react-i18next";
import {ModalLog} from "../../services/interactions/elements/modal";
import AuditTrail, {PAGES} from "../../services/interactions/audit.trail";

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: "none",
        padding: "0px",
        boxShadow: "0 3px 8px 0 rgba(0, 0, 0, 0.38)",
    }
};
Modal.setAppElement('#root');

function TeamMemberItem({member, onSelected}) {

    const handleClick = () => {
        onSelected(member);
    }

    return (
        <div className={`MemberItem`} onClick={handleClick}>
                <p className="ellipsis">
                    {member.name}
                </p>
                <p className="ellipsis">
                    {member.email}
                </p>
                <p className="capitalize">
                    {member.role}
                </p>
                <p>
                    {member.type}
                </p>
            </div>
    );
}

const _Modal = ModalLog(Modal);

function ProgramMembersDialog({type, program, openModal, closeModal}) {
    const {t} = useTranslation(['common', 'teams']);
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [members, setMembers] = useState([]);
    const [membersSelected, setMembersSelected] = useState([]);

    const [searchFocused, changeSearchFocused] = useState(false);
    const [search, changeSearch] = useState('');
    const [searchTerm] = useState(new Subject());

    const [getTeams] = useLazyQuery(gqlSchemaLibrary.teams.queries.getTeams, {
        onCompleted: (data) => {
            const _members = members;
            const _membersSelected = membersSelected;
            for (const t of data.getTeams) {
                const d = {
                    _id: t._id,
                    name: t.name,
                    role: "",
                    email: "",
                    type: "Team"
                };
                _members.push(d);
                if (program.teams.find(m => m._id === t._id)) {
                    _membersSelected.push(d);
                }
            }
            setTeams(data.getTeams);
            setMembersSelected(Object.assign([], _membersSelected));
            setMembers(Object.assign([], _members));
        },
        fetchPolicy: 'cache-and-network'
    })

    const [getUsers] = useLazyQuery(gqlSchemaLibrary.clinicians.queries.getCliniciansByOrg, {
        onCompleted: (data) => {
            const _members = members;
            const _membersSelected = membersSelected;
            for (const m of data.getCliniciansByOrg) {
                const name = m.user.bio.lastname + ', ' + m.user.bio.firstname;
                const d = {
                    _id: m._id,
                    name: name,
                    role: m.role,
                    email: m.user.email,
                    type: "User"
                };
                _members.push(d);
                if (program.users.find(u => u._id === m._id)) {
                    _membersSelected.push(d);
                }
            }
            setUsers(data.getCliniciansByOrg);
            setMembersSelected(Object.assign([], _membersSelected));
            setMembers(Object.assign([], _members));
        },
        fetchPolicy: 'cache-and-network'
    })

    useEffect(() => {
        setMembersSelected([]);
        setMembers([]);
        if (program) {
            type === 'user'? getUsers({}) : getTeams({});
        }
    }, [program, openModal])

    const [editMembersProgram] = useMutation(gqlSchemaLibrary.programs.mutation.editMembersProgram, {
        onCompleted: ({ editTeam }) => {
            const userIds = membersSelected.filter((m) => m.type === 'User').map((u) => u._id);
            const _users = users.filter((u) => {
                return userIds.includes(u._id);
            })
            const teamIds = membersSelected.filter((m) => m.type === 'Team').map((u) => u._id);
            const _teams = teams.filter((u) => {
                return teamIds.includes(u._id);
            })
            closeModal(program, _users, _teams);
        },
        onError: (e) => {
            console.error('Update Error: ', e);
        }
    });

    const closeModalEmpty = () => {
        AuditTrail.sendLog(PAGES.PROGRAM_USERS, {tag: 'button', event: 'closeModal', action: 'button close modal', program_id: program._id});
        closeModal(null, null);
    }

    const handleFocus = (bool) => {
        return () => {
            AuditTrail.sendLog(PAGES.PROGRAM_USERS, {tag: 'input', event: 'onSearch', action: 'modal input search users', program_id: program? program._id: null, focus: bool, value: search});
            changeSearchFocused(bool);
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value;
        changeSearch(value)
        searchTerm.next(value);
    }

    const onSelected = (member) => {
        const index = membersSelected.indexOf(member);
        if (index === -1) {
            AuditTrail.sendLog(PAGES.PROGRAM_USERS, {tag: 'row', event: 'onSelected', action: 'modal selected item', program_id: program? program._id: null, selected: true, type: type, value: member._id});
            membersSelected.push(member);
        }
        setMembersSelected(Object.assign([], membersSelected));
    }

    const filteredMembers = members.filter(t => {
        if (membersSelected.find((m) => m._id === t._id)) {
            return false;
        }
        if (!search) {
            return true;
        }
        const name = (t.name || '').toLowerCase();
        return name.includes(search.toLowerCase());
    }).sort((a, b) => {
        return a.name.localeCompare(b.name);
    })

    const onDeleteItem = (member) => {
        const index = membersSelected.indexOf(member);
        if (index > -1) {
            AuditTrail.sendLog(PAGES.PROGRAM_USERS, {tag: 'row', event: 'onSelected', action: 'modal selected item', program_id: program? program._id: null, selected: false, type: type, value: member._id});
            membersSelected.splice(index, 1);
        }
        setMembersSelected(Object.assign([], membersSelected));
    }

    const onSave = () => {
        let data = {
            _id: program._id
        };
        if (type === 'user') {
            data.users = membersSelected.filter((m) => m.type === 'User').map((u) => u._id);
        } else {
            data.teams = membersSelected.filter((m) => m.type === 'Team').map((u) => u._id);
        }
        AuditTrail.sendLog(PAGES.PROGRAM_DETAIL, {event: 'onClick', action: 'modal button save', program_id: program? program._id: null, type: type, ids: (type === 'user')? data.users : data.teams});
        editMembersProgram({
            variables: {
                programMembers: data
            }
        })
    }

    return (
        <_Modal
            isOpen={!!openModal}
            style={customStyles}
            onRequestClose={closeModalEmpty}
            contentLabel="Add members"
            label="MembersDialog"
            event={PAGES.PROGRAM_USERS}
            eventParams={{program_id: program? program._id: null}}>
            <div id="MembersDialog_">
                <div className="__MembersTitle">
                    <h3>Assign/Unassign {type==='user'? 'user(s)' : 'team(s)'} to program:</h3>
                    <p>{program? program.name : ''}</p>
                </div>
                <div className="__Container">
                    <p className="__MembersSubTitle">
                        <b>Assign {type==='user'? 'user(s)' : 'team(s)'}</b>
                    </p>
                    <p>
                        {t('teams:click-on-the-name-assign')}
                    </p>
                    <div className="InputComponent">
                        <input value={search} placeholder={type==='user'? t('teams:find-user') : t('teams:Find a team')} required={false} onChange={handleSearch}
                           onFocus={handleFocus(true)} onBlur={handleFocus(false)}/>
                    </div>
                    <div className="__MembersList">
                        <div className="headers">
                            <div className="selectable">
                                <p>
                                    {t('name')}
                                </p>
                            </div>
                            <div className="selectable">
                                <p>
                                    {t('email')}
                                </p>
                            </div>
                            <div className="selectable">
                                <p>
                                    {t('role')}
                                </p>
                            </div>
                            <div className="selectable">
                                <p>
                                    {t('type')}
                                </p>
                            </div>
                        </div>
                        {
                            filteredMembers.map((member, index) => (
                                <TeamMemberItem key={index} member={member} onSelected={onSelected} />
                            ))
                        }
                    </div>
                    <p className="__MembersSubTitle borderTop">
                        <b>Unassign {type==='user'? 'user(s)' : 'team(s)'}</b>
                    </p>
                    <p>
                        {t('teams:click-on-the-name-unassign')}
                    </p>
                    <div className="tags">
                        {
                            membersSelected.map((member, index) => (
                                <span key={'s-'+index} className="selectedMember">
                                    {member.name}
                                    <i className="material-icons" onClick={() => onDeleteItem(member)}>close</i>
                                </span>
                            ))
                        }
                    </div>
                    <div className="containerButtons">
                        <button className="primary gray" onClick={closeModalEmpty}>
                            {t('cancel')}
                        </button>
                        <button className="primary green" onClick={onSave}>
                            {t('save')}
                        </button>
                    </div>
                </div>
            </div>
        </_Modal>
    );
}

export default ProgramMembersDialog;
